/* Note: hasOwnProperty change: https://eslint.org/docs/rules/no-prototype-builtins */
import React, { useState, useEffect, useMemo, useTransition, useCallback, useDeferredValue } from "react";
import {
  CHANNEL_LINEUP_VIEW,
  Choice,
  KeyValuePair,
  PACKAGE_INDICATORS,
  SortOption
} from "@directv-digital/dds.services.channel-lineup-provider";
import {
  LINK_POSITION_BODY,
  fireLinkClickEvent,
  firePageLoadEvent,
  getAnalyticsAttrs
} from "@directv-digital/dds.services.analytics-service";
import { useLocationHref } from "src/hooks/useLocationHref";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import { logError } from "lib/helper/logger";

const bps = DtvTheme.breakpoints;

import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton, { IDtvButtonVariation, IDtvMode } from "@directv-digital/dds.ui.dtv-button";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import RadioButton from "@directv-digital/fusion.atoms.radio-button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import LazyLoad from 'react-lazyload';

// local components
import { CategoryHeader, ChannelRow, MyFreeInfoBlock, SearchResultsMessage, SvodChannelRow } from "./components";
import { channelColWidthStyle, ssrStyles } from "./PackageCmprGrid.styles";
import { IChannelsUI } from "../ChlupStore";
import { EmbeddedSvods, IIndicatorDataset, IPackage } from "../chnlListingPage.props";
import { consolidateSearchChannels, getMaxPackageDim, IConsolidatedSearchChannel } from "../utils/utilityFunctions";
import { zIndexMap } from "../utils/zIndexMap";
import { IChnlLineupThemeUI } from "../server/themeConfig";
import MyFreeDtvBanner, { MyFreeDtvBannerProps, IMyFreeDtvChannel } from "../components/MyFreeDtvBanner";

export interface PackageComparisonGridProps {
  channelsAZ: IChannelsUI["cmprGridChannelsAZ"];
  channelsFilteredByNumber?: IChannelsUI["cmprGridChannelsNum"];
  channelsCategorical: IChannelsUI["cmprGridChannelsCategorical"];
  myFreeDtvChannels?: IMyFreeDtvChannel[];
  categoriesAZ: KeyValuePair[];
  categoriesCategorical: KeyValuePair[];
  initViewMode?: SortOption;
  sortOptions?: SortOption[];
  searchListActive?: boolean;
  /** [WAT-11256] The active search chips, purely for the live-region announcement's
   * copy (searchAnnouncementText) — NOT used to filter anything here; every prop
   * this component filters by (channelsAZ, embeddedSvods, …) already arrives
   * pre-filtered from the caller. Optional: callers that don't pass it just get the
   * count-only announcement wording. */
  searchTerms?: Choice[];
  packages?: IPackage[];
  myFreeDirectvBanner?: MyFreeDtvBannerProps;
  pkgIndicatorIcons?: IIndicatorDataset;
  pkgIndicatorTooltipPrefixText?: IIndicatorDataset;
  addOnPromoLogoInfo?: IIndicatorDataset;
  theming?: IChnlLineupThemeUI["packagesTab"];
  noMatchPageMsg?: string;
  /** [WAT-11256] CMS-optional, same "always has copy" fallback treatment as
   * noMatchPageMsg — searchConfig?.searchErrorMsgPartial / searchErrorMsgFull,
   * ChnlListingPage.tsx. See searchErrorMsg below for which branch is which. */
  searchErrorMsgPartial?: string;
  searchErrorMsgFull?: string;
  /** [WAT-11256] Must already be search-filtered by the caller — ChnlListingPage.tsx
   * passes `filteredEmbeddedSvods`, not the raw `embeddedSvods` list. This component
   * does not filter by the active search term itself; it only gates rendering on
   * `isEmbeddedSvodsEnabled` and counts/renders whatever it's given as-is. */
  embeddedSvods?: EmbeddedSvods[];
  isEmbeddedSvodsEnabled?: boolean;
  rsnTierLegalTextObj?: any;
  myFreeConfig?: any;
  isSatelliteChlup?: boolean;
}

/**
 * @description [WAT-11256] consolidatedSearch's return shape. `rows: null` means
 * "not searching"; `rows: []` + `failed: false` means "genuinely zero matches";
 * `failed: true` always carries `rows: []`, never partial rows.
 *
 * Documentation, NOT enforcement: `tsconfig.json` sets `strictNullChecks: false`
 * and `next.config.js` sets `typescript.ignoreBuildErrors: true`, so this union
 * catches nothing at build time even with a genuine literal discriminant (`failed`
 * below is `false`/`false`/`true` per member, not the non-literal `boolean` an
 * earlier version of this type used, which admitted `{ rows: [row], failed: true }`
 * — exactly the state this type means to forbid). The three `return` statements in
 * the useMemo below are the actual boundary this invariant is enforced by.
 */
type ConsolidatedSearchState =
  | { rows: null; failed: false }
  | { rows: IConsolidatedSearchChannel[]; failed: false }
  | { rows: []; failed: true };

// [WAT-11717] A single stable reference for the "no MyFree matches" case below, so a
// non-search render (the common case) doesn't hand out a fresh `[]` literal every
// render — that identity churn was defeating searchLegendVisibility's useMemo, which
// lists this value in its dependency array and recomputed its whole scan on every
// render as a result, not just when the underlying data actually changed.
// Frozen, not just conventionally-empty: ONE instance is handed to every caller (that
// shared identity is the whole point), so a single downstream push/sort would corrupt the
// "empty" every other consumer sees. `readonly` states the contract, `Object.freeze` enforces it.
// [WAT-11774] Annotated as the row shape the scan actually reads. `readonly Channel[]` was
// inert: `myFreeSearchMatches` still inferred `any` from `channelsAZ`, so the `.indicators`
// read in the legend scan got no checking at all — the same hole the annotation on
// consolidatedSearchChannels was added to close.
const EMPTY_MYFREE_MATCHES: readonly IConsolidatedSearchChannel[] = Object.freeze([]);

function fireLoadAnalytics() {
  firePageLoadEvent({
    "page.category.applicationName": "UF", // APPLICATION_NAME,
    "page.category.pageFunction": "Other", // PAGE_FUNCTION,
    "page.category.pageOwnership": "Video", // OWNERSHIP,
    "page.pageInfo.flowCode": "Prospect", // FLOW_CODE,
    "page.pageInfo.lineOfBusiness": "Video", // LINE_OF_BUSINESS,
    "page.location.url": window.location.pathname
  });
}

const PackageComparisonGrid = (props: PackageComparisonGridProps) => {
  const {
    channelsAZ,
    channelsFilteredByNumber,
    channelsCategorical,
    myFreeDtvChannels,
    categoriesAZ,
    categoriesCategorical,
    searchListActive,
    searchTerms,
    sortOptions,
    packages,
    pkgIndicatorIcons,
    pkgIndicatorTooltipPrefixText,
    addOnPromoLogoInfo,
    myFreeDirectvBanner,
    theming,
    noMatchPageMsg,
    searchErrorMsgPartial,
    searchErrorMsgFull,
    embeddedSvods,
    isEmbeddedSvodsEnabled,
    rsnTierLegalTextObj,
    myFreeConfig,
    isSatelliteChlup
  } = props;

  const chnlPackageData = useMemo(() => packages?.map(pkg => ({
    pkgName: pkg?.packageConfig?.displayLabel,
    indIx: pkg?.packageIndicatorIndex
  })), [packages]);

  /** ------------------------------------------------------------------------------- */
  /** Table State and Effects --------------------------------------------------------
  **/
  /** useDeferredValue
   *  - used to delay the re-render of the component until the user stops typing
   *  - If viewMode is being used for a HEAVY calculation that may suspend the table, useviewMode
   *      - otherwise, use viewMode directly for instant updates
   */
  const [viewMode, setViewMode] = useState(props?.initViewMode);
  const contentViewMode = useDeferredValue(viewMode);
  // isPending is deliberately unused. The toggle IS a large render (the expanded
  // category commits its full channels-x-packages grid — the reason it is wrapped
  // in startTransition in handleCtgyHeaderClick), so on slow devices the expanded
  // content — and the header's aria-expanded flip, which is driven by the same
  // deferred state and commits WITH it — can land a beat after the tap. Accepted
  // trade-off: there is NO intermediate pending affordance during that beat. If
  // field feedback flags the lag, wire isPending into the tapped header rather
  // than making the setState synchronous again (that would undo the WAT-11290
  // INP fix).
  const [_isPending, startTransition] = useTransition();
  const locationHref = useLocationHref();

  const accByCategoryIterable = Array.from(Array(categoriesCategorical?.length));
  const [accStateByAZ, setAccStateByAZ] = useState(
    isEmbeddedSvodsEnabled ? [{ expanded: true }, { expanded: true }, { expanded: false }, { expanded: false }] : [{ expanded: true }, { expanded: false }, { expanded: false }]
  );
  const [accStateByCategory, setAccStateByCategory] = useState(
    accByCategoryIterable?.map((_, ix) => (ix <= (isEmbeddedSvodsEnabled ? 2 : 1) ? { expanded: true } : { expanded: false }))
  );

  /**
   * Channel content which is to be displayed
   * - Uses contentViewMode since it is a HEAVY calculation
   */
  const displayChannels = useMemo(() => (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS ? channelsAZ  : contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER  ? channelsFilteredByNumber : channelsCategorical), [channelsAZ, channelsCategorical, channelsFilteredByNumber, contentViewMode]);

  const displayCategories = useMemo(() => (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER ? categoriesAZ : categoriesCategorical), [categoriesAZ, categoriesCategorical, contentViewMode]);

  // [WAT-11256] AC1/AC3/AC4: an active search (a channel chip is selected) always
  // renders a single flat, deduplicated channel list — regardless of sort/view mode —
  // with the "Sort by" controls and accordion headers/collapse affordance removed.
  //
  // Parity note: the channel-lineup PAGE now answers a search this way, but the
  // package-comparison MODAL (src/components/video-lab/channel-compare-tool-v3/
  // ChnlComparisonGrid/ChnlComparisonGrid.tsx, `isSearchActive`) still implements the
  // older contract — CATEGORY+chip collapses to name grouping with NO consolidation
  // step, so a channel that's included in one package and an add-on in another still
  // renders as two rows there. That file lives in THIS repo (imported via the `@/`
  // alias, not a separate package) — bringing it in line is an intentionally deferred
  // follow-up for WAT-11256, not a regression introduced here, but it needs its own
  // tracked sub-task rather than living only in this comment; flag it on the ticket.
  //
  // MyFree DIRECTV is excluded FROM CONSOLIDATION ONLY: its indicators are synthetic
  // all-IN (every package column, chlupStore.ts) and are only truthful next to the
  // MyFree label + satellite caveat, which the flat consolidated list doesn't render.
  // It is NOT dropped from the search results — see myFreeSearchMatches below, which
  // renders it as its own labelled, non-collapsible block and counts toward
  // showSearchNoMatch, so a MyFree-only match doesn't produce a false "no results".
  //
  // Wrapped in try/catch (mirrors the retained category loops' degrade-to-empty
  // pattern below) so a malformed feed row costs the search list, not the page — this
  // render lives under ChnlListingPage.tsx's try/<ErrorDisplay>, but a child's render
  // throw during commit propagates past a parent that has already returned. Returns
  // `{ rows, failed }` rather than `[]` on failure: an empty array is also the
  // legitimate "genuinely zero matches" result, and conflating the two would show the
  // customer the CMS "no channels match" copy for what is actually a bug — see
  // `searchConsolidationFailed` below.
  const consolidatedSearch = useMemo((): ConsolidatedSearchState => {
    if (!searchListActive) {
      return {
        rows: null,
        failed: false
      };
    }
    try {
      return {
        rows: consolidateSearchChannels(channelsAZ, myFreeConfig?.myFreeCategory?.key),
        failed: false
      };
    } catch (error) {
      logError("PackagesGrid-consolidateSearchChannels", error, { componentId: "PackageComparisonGrid" });
      return {
        rows: [],
        failed: true
      };
    }
  }, [searchListActive, channelsAZ, myFreeConfig?.myFreeCategory?.key]);
  const consolidatedSearchChannels = consolidatedSearch?.rows;
  const searchConsolidationFailed = consolidatedSearch?.failed ?? false;

  // MyFree DIRECTV search matches: `channelsAZ` here is already the parent's
  // search-filtered map (ChnlListingPage's filteredGridChannelsAZ), so this bucket —
  // when the MyFree category key resolves — already only contains channels matching
  // the active search term. No further dedupe needed: the store pushes each MyFree
  // channel into this bucket at most once (chlupStore.ts).
  const myFreeSearchKey = myFreeConfig?.myFreeCategory?.key;
  // A plain property read, not a useMemo — the result is only ever `.length`-ed,
  // `.map`-ed inline below, and read into searchLegendVisibility's dep array, so
  // there's no recomputation a useMemo here would actually save. `EMPTY_MYFREE_MATCHES`
  // (not a fresh `[]`) on the falsy branch keeps that dependency's identity stable
  // across renders where there's nothing to report — see its own comment above.
  const myFreeSearchMatches: readonly IConsolidatedSearchChannel[] = searchListActive && myFreeSearchKey ? channelsAZ?.[myFreeSearchKey] || EMPTY_MYFREE_MATCHES : EMPTY_MYFREE_MATCHES;
  const hasMyFreeMatches = (myFreeSearchMatches?.length ?? 0) > 0;

  /** ------------------------------------------------------------------------------- */
  /** Dynamic Styles ----------------------------------------------------------------
   **/
  const packagesLength = useMemo(() => packages?.length || 4, [packages]);
  const mainColWidthStyle = useMemo(() => ({ // subtract 130px for the channel column
    flexBasis: `calc((100% - 215px) / ${packagesLength})`,
    width: `calc((100% - 215px) / ${packagesLength})`,
    [bps.up(bps.values.md)]: {
      flexBasis: `calc((100% - 150px) / ${packagesLength})`,
      width: `calc((100% - 150px) / ${packagesLength})`
    },
    [bps.up(350)]: {
      flexBasis: `calc((100% - 130px) / ${packagesLength})`,
      width: `calc((100% - 130px) / ${packagesLength})`
    },
    [bps.down(350)]: {
      flexBasis: `calc((100% - 110px) / ${packagesLength})`,
      width: `calc((100% - 110px) / ${packagesLength})`
    }
  }), [packagesLength]);

  const svodRowWidth = useMemo(() => ({ // subtract 130px for the channel column
    flexBasis: `calc(100% - 215px)`,
    width: `calc(100% - 215px)`,
    [bps.up(bps.values.md)]: {
      flexBasis: `calc(100% - 150px)`,
      width: `calc(100% - 150px)`
    },
    [bps.up(350)]: {
      flexBasis: `calc(100% - 130px)`,
      width: `calc(100% - 130px)`
    },
    [bps.down(350)]: {
      flexBasis: `calc(100% - 110px)`,
      width: `calc(100% - 110px)`
    }
  }), [packagesLength]);

  /** -----------------------------------------------
   * Styles re-calculated pon viewport width change */
  let tableHeaderRef: HTMLTableSectionElement;
  const [verticalPkgNamesOn, setVerticalPkgNamesOn] = useState<boolean>(false);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [maxVertPkgDim, setMaxVertPkgDim] = useState<number>(0);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  const updateVerticalHeaders = (thElem: HTMLElement) => {
    // get the max width or height dimension of package names
    const maxPkgTextDim = getMaxPackageDim(
      thElem,
      ".package-text-container",
      ".package-name",
      ".package-chnl-count"
    );
    setMaxVertPkgDim(maxPkgTextDim);
    const containerWidth =
      thElem?.querySelectorAll(".package-container")?.[0]
        ?.getBoundingClientRect()?.width;

    // if vertical headers is on and the widest text is smaller than containerWidth, turn off
    if (verticalPkgNamesOn === true) {
      if ((maxPkgTextDim < containerWidth - 24)) {
        setVerticalPkgNamesOn(false);
      }
      return;
    }
    // if vertical headers is on and the longest text is longer than the containerWidth, keep on, otherwise turn off
    if (maxPkgTextDim > containerWidth - 24) {
      setVerticalPkgNamesOn(true);
      return;
    }
    setVerticalPkgNamesOn(false);
  }

  useEffect(() => {
    function onWindowResize() {
      const tableHeaderElem = tableHeaderRef || document.getElementById("chlup-table-head");
      if (tableHeaderElem) {
        updateVerticalHeaders(tableHeaderElem);
        setHeaderHeight(pv => (tableHeaderElem?.clientHeight > 0 ? tableHeaderElem?.clientHeight : pv));
      }
    };
    let scrollRafId;
    function onScroll() {
      if (scrollRafId) {
        cancelAnimationFrame(scrollRafId);
      }
      scrollRafId = requestAnimationFrame(() => {
        const tableHeaderElem = tableHeaderRef || document.getElementById("chlup-table-head");
        if (tableHeaderElem) {
          setHasScrolled(window?.scrollY > tableHeaderElem?.offsetTop);
        }
      });
    }

    window.addEventListener("resize", onWindowResize);
    window.addEventListener("scroll", onScroll);
    onWindowResize();
    onScroll();
    fireLoadAnalytics();

    return () => {
      window.removeEventListener("resize", onWindowResize);
      window.removeEventListener("scroll", onScroll);
    }
  }, []);

  // Update header height, check for sticky header, whenever something can change the
  // sort cell's rendered height: verticalHeaders switching, OR search toggling —
  // [WAT-11256] the "Sort by" label + radiogroup unmount entirely while
  // searchListActive is true (below), so the sticky TableHead can get shorter/taller
  // across that transition. CategoryHeader's sticky `top` offset is this exact value
  // (headerHeight), so an unmeasured transition leaves pinned category headers
  // sitting at a stale offset until the next window resize happens to fire.
  useEffect(() => {
    const tableHeaderElem = tableHeaderRef || document.getElementById("chlup-table-head");
    if (tableHeaderElem) {
      setHeaderHeight(pv => (tableHeaderElem?.clientHeight > 0 ? tableHeaderElem?.clientHeight : pv));
    }
  }, [verticalPkgNamesOn, searchListActive]);

  /** -------------------------------------------------------------------------
   * UI event handlers
   */
  // Reset the browse-view accordions to their DEFAULT state (the same mixed
  // open/closed pattern as the useState initializers above — the first one or two
  // sections open, the rest closed; NOT all-collapsed) whenever search clears or the
  // sort mode changes. No accordions mount at all while a search IS active —
  // TableBody's searchListActive branch renders a flat list with no
  // Accordion/CategoryHeader — so there is nothing to open and this effect
  // early-returns rather than computing a state update nothing reads.
  useEffect(() => {
    if (searchListActive) {
      return;
    }
    if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER) {
      setAccStateByAZ(() => (isEmbeddedSvodsEnabled ? [{ expanded: true }, { expanded: true }, { expanded: false }, { expanded: false }] : [{ expanded: true }, { expanded: false }, { expanded: false }]));
    } else if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CATEGORY) {
      setAccStateByCategory(() => accByCategoryIterable.map((_, ix) => (ix <= (isEmbeddedSvodsEnabled ? 2 : 1) ? { expanded: true } : { expanded: false })));
    }
  }, [searchListActive, viewMode]);

  const handleChangeSortOption = sortKey => {
    const option = sortOptions.find((s: SortOption) => s.key === Number(sortKey));
    startTransition(() => {
      setViewMode(option);
    });
    // setViewMode(option);

    fireLinkClickEvent({
      linkDestination: "",
      linkName: `Display_${option?.value}`,
      linkPosition: LINK_POSITION_BODY
    });
  };

  /**
   * @description Compensating smooth scroll so closing a long accordion under a
   * sticky header does not leave the page jumped past the summary. Must run
   * synchronously in the click handler so it reads PRE-toggle layout: a deferred
   * relative scrollBy would re-read the live scroll position after the commit,
   * where browser clamping on collapse can shift its reference frame. The INP win
   * comes from deferring the heavy toggle render in handleCtgyHeaderClick, not
   * from this scroll — a smooth scrollBy animates over hundreds of ms regardless.
   * Sticky detection: offsetTop > 0 means the summary element is currently pinned.
   * @param id - of the sticky header element to compensate for
   */
  const scrollOntoStickyHeader = (id: string) => {
    const accSummaryElem = (document.querySelector(`#${id}`) as HTMLElement);
    if (accSummaryElem && accSummaryElem?.offsetTop > 0) {
      window.scrollBy({
        top: -accSummaryElem?.offsetTop,
        behavior: "smooth"
      });
    }
  };

  /** Open/Close individual category on header click.
   * INP: the toggled category renders its full channels-x-packages grid, so the state
   * update is wrapped in startTransition (like handleChangeSortOption) to keep
   * that large commit off the interaction's blocking window. */
  const handleCtgyHeaderClick = useCallback(index => {
    if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER) {
      scrollOntoStickyHeader(`CHLUP-az-accsummary-${index}`);
      startTransition(() => {
        setAccStateByAZ(prevAcc => ({
          ...prevAcc,
          [index]: { expanded: !prevAcc[index]?.expanded }
        }));
      });
    } else if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CATEGORY) {
      scrollOntoStickyHeader(`CHLUP-ctg-accsummary-${index}`);
      startTransition(() => {
        setAccStateByCategory(prevAcc => ({
          ...prevAcc,
          [index]: { expanded: !prevAcc[index]?.expanded }
        }));
      });
    }
    // Functional setState updates (prevAcc => …) — no read of accStateByAZ/Category, so
    // they're intentionally absent from deps to keep the callback identity stable
    // (startTransition from useTransition is identity-stable too).
  }, [contentViewMode]);

  const packageIndicatorIconMap = useMemo(() => ({
    [PACKAGE_INDICATORS.ADDON]: {
      src: pkgIndicatorIcons.addon,
      color: theming?.indicatorIcons?.addon,
      tooltipText: `${pkgIndicatorTooltipPrefixText.addon} in`,
      alt: (pkgName: string) => `${pkgIndicatorTooltipPrefixText.addon} in ${pkgName}`
    },
    [PACKAGE_INDICATORS.IN]: {
      src: pkgIndicatorIcons.included,
      color: theming?.indicatorIcons?.included,
      tooltipText: `${pkgIndicatorTooltipPrefixText.included} in`,
      alt: (pkgName: string) => `${pkgIndicatorTooltipPrefixText.included} in ${pkgName}`
    },
    [PACKAGE_INDICATORS.PROMO]: {
      src: pkgIndicatorIcons.promo,
      color: theming?.indicatorIcons?.promo,
      tooltipText: `${pkgIndicatorTooltipPrefixText.promo} in`,
      alt: (pkgName: string) => `${pkgIndicatorTooltipPrefixText.promo} in ${pkgName}`
    }
  }), [pkgIndicatorIcons, pkgIndicatorTooltipPrefixText, theming?.indicatorIcons]);

  // console.debug(
  //   "[PackagesGrid] rendering...", {
  //     channelColWidthStyle: channelColWidthStyle,
  //     mainColWidthStyle: mainColWidthStyle,
  //     verticalPkgNamesOn:verticalPkgNamesOn,
  //     headerHeight: headerHeight,
  //     accStateByAZ: accStateByAZ,
  //     accStateByCategory: accStateByCategory,
  //     channelsAZ: channelsAZ,
  //     channelsCategorical: channelsCategorical,
  //     hasScrolled: hasScrolled,
  // });

  // [WAT-11256] embeddedSvods ROWS are only rendered when the feature flag is on —
  // the flag and the CMS data are independent (an author can populate SVODs while the
  // flag stays off for a pre-launch state), and every OTHER SVOD row-render path in
  // this tree is flag-gated the same way (ChnlListingPage.tsx only unshifts the
  // INCLUDED_SUBSCRIPTIONS category `if (isEmbeddedSvodsEnabled)`). This gates the
  // ROW render; the SVOD chip source itself (`searchChoicesSvods`) is gated
  // separately in chlupStore.ts's initializeChlupStore (`data?.isEmbeddedSvodsEnabled
  // ? getSearchChoices(...) : []`), fed by isEmbeddedSvodsEnabled now being passed
  // into the reducer's init data from ChnlListingPage.tsx.
  const embeddedSvodsCount = embeddedSvods?.length ?? 0;
  const hasSvodMatches = isEmbeddedSvodsEnabled && embeddedSvodsCount > 0;
  // Reused below (searchLazyLoadCutoff, searchResultCount) — hoisted so all three
  // agree on "how many SVOD rows are actually rendered" from one place.
  const svodMatchCount = hasSvodMatches ? embeddedSvodsCount : 0;
  // [WAT-11256] Eager-render (no LazyLoad) cutoff for the consolidated search list.
  // The SVOD rows AND the MyFree rows above/below both already render eagerly (no
  // LazyLoad wrapper on either) and consume part of the same first-paint budget, so
  // this SUBTRACTS both counts (not adds — adding raises the eager count on top of
  // rows that are already eager, exactly the eager-first-paint cost this cutoff
  // exists to bound). `cIx` in the map below comes from consolidatedSearchChannels'
  // own index and excludes the SVOD/MyFree rows entirely — they each have their own
  // independent index from a separate `.map()` — which is the non-obvious fact this
  // whole calculation hinges on. Clamped to 0 so a large authored SVOD/MyFree list
  // can't push the cutoff negative (which would lazy-load row 0). Hoisted here (not
  // recomputed per row) since it's loop-invariant.
  const searchLazyLoadCutoff = Math.max(0, 15 - svodMatchCount - (myFreeSearchMatches?.length ?? 0));
  // [WAT-11256] A MyFree-only match must not read as "no results" — see
  // myFreeSearchMatches above: MyFree is excluded from consolidation (synthetic
  // all-IN indicators need its own label/context) but IS still a real search result,
  // rendered as its own block below and counted here.
  const showSearchNoMatch = !searchConsolidationFailed &&
    (consolidatedSearchChannels?.length ?? 0) < 1 &&
    !hasSvodMatches &&
    !hasMyFreeMatches;
  // [WAT-11256 round-6 S1] SVOD and MyFree matches are computed independently of
  // consolidateSearchChannels and cannot be affected by its throw — used below to
  // pick partial- vs. total-failure copy, and to keep the "Search results" heading
  // mounted when there's something real underneath it.
  const hasOtherMatches = hasSvodMatches || hasMyFreeMatches;
  // [WAT-11717] Which of the three legend items (Included / Add-on / Promo) the
  // search-results header shows — a message only renders when a channel actually
  // carrying that indicator is present among the rows this branch WILL render. [WAT-11774]
  // "will render" is deliberate: rows past `searchLazyLoadCutoff` are LazyLoad-wrapped, so
  // an add-on carrier at index 30 lights its legend item before its icon is in the DOM. The
  // scan is over the whole result set on purpose — the legend describes the result, not the
  // current viewport — but it is not a claim about what is mounted right now. The rows are:
  // the SVOD
  // rows (when shown), the consolidated channel list, and the MyFree matches (whose
  // indicators are synthetic all-IN — see consolidatedSearch above — so they can only
  // ever contribute "included"). Scanning `indicators` arrays directly, not the
  // per-row `hasAddOnOrPromoIndicator`/`isAddOnOnlyRow` booleans computed later in the
  // render loop below, since those are derived per-row for styling and this needs a
  // single pass across every row before any of them render.
  const searchLegendVisibility = useMemo(() => {
    // [WAT-11774] Browse renders no legend, so there is nothing to compute — and the scan
    // could reach logError there, reporting a feed-shape problem for UI that never mounts.
    // Mirrors consolidatedSearch's early return above.
    if (!searchListActive) {
      return {
        included: false,
        addon: false,
        promo: false
      };
    }
    const seen = new Set<PACKAGE_INDICATORS>();
    // [WAT-11774] `sawAnyRow` is gone. It existed only to disambiguate "rows scanned but
    // nothing recognised" from "no rows at all" for a fail-open that is now keyed on
    // malformed data instead, so keeping it would leave dead state behind.
    let sawMalformedIndicators = false;
    const collect = (indicators?: (PACKAGE_INDICATORS | null | undefined)[]) => {
      // [WAT-11717] A non-array `indicators` (feed shape drift) would throw on
      // `.forEach` INSIDE this component's own render — which the parent's
      // <ErrorDisplay> boundary cannot catch, the same hazard consolidatedSearch above
      // is wrapped in a try/catch for. Bailing leaves `seen` empty while `sawAnyRow`
      // stays true, so the fail-open below turns it into "show all three" — the same
      // safe answer every other unexpected shape gets here, rather than a blank legend.
      // [WAT-11774] Absent is NOT malformed. `indicators` is optional on this component's
      // own prop declaration and on collect's own parameter type, yet a nullish value used
      // to set the malformed flag, fire logError — in the BROWSE view too, where the legend
      // never mounts — and fail the legend open to all three items, which is the exact
      // over-listing defect WAT-11717 set out to fix. The sibling coercion in
      // consolidateSearchChannels already treats absent as "no indicators", silently.
      if (indicators === null || indicators === undefined) {
        return;
      }
      if (!Array.isArray(indicators)) {
        sawMalformedIndicators = true;
        return;
      }
      indicators.forEach(indicator => {
        if (
          indicator === PACKAGE_INDICATORS.IN ||
          indicator === PACKAGE_INDICATORS.ADDON ||
          indicator === PACKAGE_INDICATORS.PROMO
        ) {
          seen.add(indicator);
        }
      });
    };
    if (hasSvodMatches) {
      embeddedSvods?.forEach(svod => collect(svod?.indicators));
    }
    // [WAT-11717] Annotated: `consolidatedSearch.rows` is a union whose failure member
    // is the empty-tuple type `[]`, so an un-annotated forEach callback infers `never`
    // and the `.indicators` read below gets NO type checking at all.
    consolidatedSearchChannels?.forEach((channel: IConsolidatedSearchChannel) => collect(channel?.indicators));
    if (hasMyFreeMatches) {
      myFreeSearchMatches?.forEach(channel => collect(channel?.indicators));
    }
    // [WAT-11717] Fail OPEN (show all three) when rows were actually scanned but NONE
    // of them carried a recognized indicator — that shape only happens on malformed or
    // unexpectedly-structured data, not on a genuine "no add-on/promo channels in this
    // search" result (which still leaves `included` true from the plain-included rows
    // every other search result carries). All-false is ALSO the legitimate "nothing to
    // show" state for this same Set, so it can't be trusted as the safe default for the
    // unexpected-shape case too: that would silently hide the legend explaining
    // indicator icons that are still visibly on screen. `sawAnyRow` false here (no rows
    // to have scanned in the first place) falls through to the normal, all-false
    // result, which is correct — the caller doesn't mount this UI in the no-match case.
    // [WAT-11717] Say so when the bail above fired. Failing open renders a PLAUSIBLE
    // legend, not a broken one, so a feed-shape regression here would look exactly like a
    // search that genuinely has no add-on/promo channels and would surface nowhere. Logged
    // once per memo run, not per row, and in the same shape this component already uses at
    // consolidateSearchChannels' catch.
    if (sawMalformedIndicators) {
      logError(
        "PackagesGrid-searchLegendVisibility",
        new Error("non-array `indicators` on a search row — legend failing open"),
        { componentId: "PackageComparisonGrid" }
      );
    }
    // [WAT-11774] Keyed on `sawMalformedIndicators`, NOT on `seen.size === 0`. The old
    // condition also fired for WELL-FORMED data: an RSN row without `packageNames` gets a
    // legitimate all-null `indicators` array (raw `packageNames` reaches getIndicators while
    // `chnlPackages` uses the fallback) and still renders — so searching for one advertised
    // all three legend icons above a row showing none. A well-formed scan that recognises no
    // indicator now produces a hidden legend, which is the honest answer; only genuinely
    // malformed data still fails open, because there the legend explains icons that may well
    // be on screen.
    if (sawMalformedIndicators) {
      return {
        included: true,
        addon: true,
        promo: true
      };
    }
    return {
      included: seen.has(PACKAGE_INDICATORS.IN),
      addon: seen.has(PACKAGE_INDICATORS.ADDON),
      promo: seen.has(PACKAGE_INDICATORS.PROMO)
    };
  }, [searchListActive, hasSvodMatches, embeddedSvods, consolidatedSearchChannels, hasMyFreeMatches, myFreeSearchMatches]);
  // [WAT-11256 round-6 BLOCKER] Deliberately flag-INDEPENDENT — this is the BROWSE
  // empty-state, not the search one. Base (origin/devdeploy/next:691) gates this on
  // raw `embeddedSvods.length` alone, with no `isEmbeddedSvodsEnabled` check. A prior
  // round rewrote it to reuse `hasSvodMatches` "for consistency" with the search
  // branch's SVOD-gating, but that silently made flag-OFF (production) behavior
  // depend on data that was previously irrelevant: with the flag off and SVODs
  // authored ahead of launch (ChnlListingPage.tsx's `filteredEmbeddedSvods` returns
  // the raw CMS list untouched while browsing — the flag only gates the A-Z category
  // INSERT, never this gate), a populated browse grid could get replaced by the CMS
  // "no channels match" copy the moment any ONE sibling map (channelsAZ /
  // channelsFilteredByNumber / categoriesCategorical) is empty. Flag-OFF must stay
  // byte-identical to prod, so this reverts to the raw count prod itself uses —
  // `hasSvodMatches` is still correct for the SEARCH branch above, where SVOD rows
  // actually only render when the flag is on.
  const showBrowseNoMatch = embeddedSvodsCount < 1 && (
    Object.keys(channelsAZ || {})?.length === 0 ||
    Object.keys(channelsFilteredByNumber || {})?.length === 0 ||
    !categoriesCategorical?.length
  );
  // [WAT-11256] Every other TableBody branch renders `tr > td` (valid table markup);
  // gridMessageText is the bare message node, gridMessage wraps it in that same
  // `tr > td` for callers that render it as TableBody's direct child (noMatchElement
  // below, and the browse no-match branch). The search-failure text further down
  // reuses gridMessageText bare, since it's already inside the search branch's own
  // tr/td and wrapping it again would nest an extra table row inside a cell.
  const gridMessageText = (content: string) => (
    <DtvText
      content={content}
      variant="body1"
      sx={{
        fontSize: {
          xs: "16px",
          md: "20px"
        },
        color: theming?.gridText,
        textAlign: "center",
        margin: "48px 16px"
      }}
    />
  );
  // [WAT-11256 round-6 S3] `noMatchPageMsg` is CMS-optional
  // (searchConfig?.noResultsMsgPackagesGrid, ChnlListingPage.tsx) — with no fallback,
  // an unauthored field renders an empty box AND announces an empty string in the
  // live region. Same "always has copy" treatment searchErrorMsg already had.
  const noMatchMsg = noMatchPageMsg || "No channels match your search.";
  const noMatchElement = (
    <DtvBox component="tr" sx={{ background: theming?.gridBackground }}>
      <DtvBox component="td" sx={ssrStyles.grid.tdFullWidthOverride}>
        {gridMessageText(noMatchMsg)}
      </DtvBox>
    </DtvBox>
  );
  // [WAT-11256] A caught consolidateSearchChannels() throw is NOT the same UI state as
  // a genuine zero-result search — showing noMatchPageMsg for both tells the customer
  // "we don't carry that channel" when the real cause is a bug, and leaves no signal
  // for us to act on (logError above does that part). Deliberately distinct, English
  // literal copy — there is no Storyblok field for this state. [round-6 S1] Distinct
  // wording for "some results survived" vs. "nothing rendered at all" — the SVOD/
  // MyFree blocks below are unaffected by this failure, so telling a screen-reader
  // user the search totally failed while rows are visibly present is itself a bug.
  const searchErrorMsg = hasOtherMatches ?
    (searchErrorMsgPartial || "Some of your search results couldn't be loaded. The results below may be incomplete.") :
    (searchErrorMsgFull || "We're having trouble loading your search results. Please try your search again.");

  // [WAT-11256] The one and only live-region announcement for search: a SINGLE node
  // that exists on every render (searchListActive true or false), outside the
  // searchListActive ? … : … branch, so a screen reader is already observing it
  // before the first search mutation — a region that mounts together with its
  // content can't announce that content (SearchResultsMessage no longer tries to).
  // Visually hidden via clip/absolute positioning, NOT display:none/visibility:hidden
  // — SearchListDropdown's existing role="alert" wraps display:none text and
  // announces nothing; that mistake is exactly what this avoids.
  const searchResultCount = (consolidatedSearchChannels?.length ?? 0) +
    svodMatchCount +
    (myFreeSearchMatches?.length ?? 0);
  // [WAT-11256 S3] Two consecutive searches that happen to return the same NUMBER of
  // results (e.g. "ESPN" then "HBO", both 2 hits) produced byte-identical announcement
  // text — a screen reader's live region only re-announces on a text CHANGE, so the
  // second search went silent. Folding in the search terms themselves (not just the
  // count) means the text differs whenever the actual result SET differs, even at an
  // unchanged count. Falls back to the count-only wording when the caller doesn't pass
  // searchTerms, so existing callers/tests that don't wire it through are unaffected.
  // [round-6 S4] The same folding now also applies to the no-match and error branches
  // — those were the two places two DIFFERENT zero-result/failed searches still
  // produced byte-identical announcement text and went silent to screen readers.
  // [WAT-11256 round-6 nit] Capped at 3 named chips — this region is
  // `aria-atomic="true"`, so an uncapped join re-reads the ENTIRE chip list on every
  // add/remove at 5+ chips, with no way for a screen-reader user to skip ahead.
  const searchTermNames = searchTerms?.map(term => term?.displayText).filter(Boolean) ?? [];
  const searchTermsLabel = searchTermNames.length > 3 ?
    `${searchTermNames.slice(0, 3).join(", ")}, and ${searchTermNames.length - 3} more` :
    searchTermNames.join(", ");
  // [WAT-11774] ONE element for both mount sites. The two branches carried six hand-synced
  // props each, so the partial-failure mount's legend props could silently diverge from the
  // normal one's — and nothing would have caught it. Follows the existing noMatchElement
  // pattern above. Now they cannot disagree by construction.
  const searchResultsMessageElement = (
    <SearchResultsMessage
      id="chlup-search-results-message"
      addOnPromoLogoInfo={addOnPromoLogoInfo}
      theming={theming?.sectionHeaders}
      showIncludedLegend={searchLegendVisibility.included}
      showAddOnLegend={searchLegendVisibility.addon}
      showPromoLegend={searchLegendVisibility.promo}
    />
  );
  const searchAnnouncementText = !searchListActive ? "" :
    searchConsolidationFailed ?
      `${searchErrorMsg}${searchTermsLabel ? ` (search: "${searchTermsLabel}")` : ""}${searchResultCount > 0 ? ` ${searchResultCount} channel${searchResultCount === 1 ? "" : "s"} shown.` : ""}` :
      showSearchNoMatch ?
        `${noMatchMsg}${searchTermsLabel ? ` No results for "${searchTermsLabel}".` : ""}` :
        searchTermsLabel ?
          `${searchResultCount} channel${searchResultCount === 1 ? "" : "s"} found for "${searchTermsLabel}".` :
          `${searchResultCount} channel${searchResultCount === 1 ? "" : "s"} found for your search.`;

  return (
    // goodbye old friend
    <DtvBox id="chluNational-1" sx={ssrStyles.tableRootContainer}>
      <DtvBox
        id="chlup-search-live-region"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        // [WAT-12043] Canonical `visuallyHidden` from `@mui/utils` —
        // same source of truth used by ChannelSearch's live region so
        // the two sr-only style blocks can't drift on a copy edit.
        sx={visuallyHidden}
      >
        {searchAnnouncementText}
      </DtvBox>
      <Table id="ChannelLineup-Table" aria-label="Channel Lineup Table">
        <TableHead id="chlup-table-head"
          ref={ref => {
 if (ref) {
 tableHeaderRef = ref;
}
}}
          sx={{
            width: "100%",
            zIndex: zIndexMap.TableHead,
            position: "sticky",
            top: '0px',
            background: theming?.headerScrollHideBg || "#fff"
        }}>
          <DtvBox
            id="ChannelLineup-PackagesHeader"
            component="tr"
            style={{
              ...ssrStyles.tablerowContainer,
              background: hasScrolled || theming?.isVariablePageBg ?
                theming?.headerScrollHideBg :
                theming?.headerBackground || theming?.headerScrollHideBg,
              ...(hasScrolled || theming?.isVariablePageBg) && myFreeDirectvBanner && {
                boxShadow: `0px 0px 10px 10px ${theming?.headerScrollHideBg}`
              },
              transition: "box-shadow 200ms ease-in-out",

              paddingBottom: "8px"
          }}>
            <DtvBox
              key="chlup-sort"
              id="chlup-sort-container"
              // CHANNELS is the leftmost column-header cell of the grid.
              // Native <th> + scope="col" survives even though the ARIA
              // table roles on the rows themselves are deferred (see the
              // AMP109 rationale block in ChannelRow.tsx): the CHANNELS
              // cell still describes the column visually, and the native
              // th role doesn't rely on the deferred row semantics.
              // There is no matching scope="row" on the rows — they're
              // divs, not rowheaders — so the audit-recommended pairing
              // isn't complete yet; that's part of the deferred AMP109
              // follow-up.
              component="th"
              scope="col"
              sx={{
                ...ssrStyles.grid.tdFullWidthOverride,
                ...ssrStyles.header.sortContainer,
                ...channelColWidthStyle,
                outline: theming?.sort?.border || "none",
                background: theming?.sort?.background,
                // AC1: sortContainer's default alignItems:center vertically centers the
                // Paper within the (taller, stretched) cell. With "Sort by" removed during
                // search the Paper is short, so centering leaves it floating mid-cell —
                // bottom-align the whole block instead so CHANNELS sits bottom-left, level
                // with the package tiles.
                ...(searchListActive && { alignItems: "flex-end !important" })
            }}>
              <Paper square elevation={0}
                sx={{
                  // Note: the bottom-alignment during search comes entirely from the
                  // parent td's `alignItems: flex-end` override above — this Paper is
                  // content-sized (not stretched), so a justifyContent override here
                  // has no free main-axis space to distribute and would be a no-op.
                  ...ssrStyles.header.sortPaper
              }}>
                <DtvText content="CHANNELS"
                  // [WAT-12043 / YFH-AMP111] CHANNELS is the visible section
                  // heading for the CHANNELS column in the Signature Packages
                  // tab — render as <h2> so it sits at the correct level
                  // beneath the page <h1> ("Stream all your favorite
                  // channels").
                  component="h2" variant="body3" color="white.main"
                  sx={{
                    // Figma type spec (Desktop/Subhead/Subhead 2 medium): 24px desktop,
                    // 20px tablet & mobile (tablet and mobile share the same style). The
                    // Figma frame supplying this spec is the search-behavior-updates
                    // canvas, not a browse-view artifact, so lineHeight/letterSpacing are
                    // gated to searchListActive like every other AC1 change here — the
                    // no-search header stays byte-identical to prod pending a design
                    // sign-off on whether it should restyle too. A single em-based
                    // letterSpacing (rather than two derived px values) self-scales with
                    // fontSize instead of needing to be re-derived if the sizes ever change.
                    fontFamily: "roboto-regular",
                    fontWeight: 500,
                    fontSize: {
                      xs: "20px",
                      md: "24px"
                    },
                    ...(searchListActive && {
                      lineHeight: 1.3,
                      letterSpacing: "-0.02em"
                    }),
                    color: theming?.sort?.textColor
                  }}
                />
                {/* AC1: "Sort By" section is removed entirely while a search is active */}
                {!searchListActive && (
                  <>
                    <DtvText content="Sort by"
                      id="chlup-sort-label"
                      component="p" variant="body3"
                      color="white.main"
                      sx={{
                        margin: "8px 0px",
                        color: theming?.sort?.textColor
                      }}
                    />
                    <DtvBox
                      sx={ssrStyles.header.radioButtonOverrides}
                      role="radiogroup"
                      aria-labelledby="chlup-sort-label"
                    >
                      {sortOptions?.length > 0 && <RadioButton
                        variant="Radio Label"
                        mode={theming?.sort?.mode as IDtvMode}
                        analytics={{
                          tag: "Radiobutton",
                          event: 'Checked',
                          event_name: 'Radiobutton'
                        }}
                        radiobuttonoptions={sortOptions?.map(op => ({
                          label: op.value,
                          value: op.key,
                          checked: viewMode?.key === op.key,
                          disabled: false,
                          id: `chlup-sort-${op.key}`,
                          name: 'chlup-sort',
                          linkName: `sortby_${op.value}`,
                          linkPosition: 'panel_channel lineup',
                          linkDestination: locationHref
                        }))}
                        onChange={e => handleChangeSortOption(e?.target?.value) }
                      />}
                    </DtvBox>
                  </>
                )}
              </Paper>
            </DtvBox>
            {packages && packages?.map((packag, pkgInd) => {
              try{
              const {
                name,
                packageConfig,
                pkgTextColor,
                pkgBgColor,
                ctaProps
              } = packag;

              return (
                // Native <th scope="col"> — each package tile is the
                // column header cell. Note the rows below render as
                // <div>s (not <td>s), so the column<->cell pairing the
                // audit expects isn't complete today; that's the
                // deferred AMP109 follow-up.
                <DtvBox key={`chlup-pkg-${name}`}
                  className="package-container" // !! for vertical headers
                  component="th"
                  scope="col"
                  justifyContent="center" alignItems="center"
                  sx={{
                    ...ssrStyles.grid.tdFullWidthOverride,
                    borderRadius: "8px 8px 0px 0px",
                    border: "none",
                    padding: "0px 4px",
                    display: "flex",
                    alignItems: "end", // flex to bottom of header
                    ...mainColWidthStyle
                }}>
                  <Paper
                    square
                    elevation={1}
                    sx={{
                      background: theming?.packageTiles?.[pkgInd]?.background || pkgBgColor,
                      outline: theming?.packageTiles?.[pkgInd]?.border || "none",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      borderRadius: "8px 8px 0px 0px",
                      padding: verticalPkgNamesOn ? "12px 1px 9px 1px" : "20px 15px",
                      [bps.between(bps.values.sm, bps.values.md)]: {
                        padding: verticalPkgNamesOn ? "16px 6px" : "20px 12px"
                      },
                      [bps.down(860)]: {
                        height: "237px"
                      },
                      [bps.up(bps.values.md)]: {
                        padding: verticalPkgNamesOn ? "16px 8px" : "20px 15px 15px 15px"
                      },
                      // WAT-11241: hold 183px from 1200 upward so cards stay level with the CHANNELS header
                      [bps.up(1200)]: {
                        height: "183px",
                        justifyContent: "center"
                      },
                      ...(verticalPkgNamesOn ? {
                        justifyContent: "flex-end",
                        alignItems: "center"
                      } : {
                        justifyContent: "space-around",
                        alignItems: "flex-start"
                      })
                  }}>
                    <DtvGrid
                      className="package-text-container" // !! for vertical headers
                      container
                      sx={{
                        width: 'fit-content',
                        height: 'fit-content',
                        display: "block",
                        ...ssrStyles.header.packageFontStyles,
                        ...(verticalPkgNamesOn && {
                          flexDirection: "column !important",
                          writingMode: "vertical-rl !important",
                          textOrientation: "mixed !important",
                          transform: "rotate(180deg) !important",
                          paddingTop: "8px",
                          height: maxVertPkgDim ? `${maxVertPkgDim }px !important` : "140px"
                        })
                      }}>
                      <DtvBox sx={{ ...verticalPkgNamesOn ? { height: "100%" } : { width: "100%" } }}>
                        <DtvText
                          content={packageConfig?.displayLabel || name}
                          className="package-name" // !! for vertical headers
                          color={pkgTextColor}
                          sx={{
                            color: theming?.packageTiles?.[pkgInd]?.packageNameText,
                            textAlign: "left",
                            fontWeight: "600 !important",
                            whiteSpace: "nowrap",
                            fontSize: "20px !important",
                            [bps.down(bps.values.md)]: {
                              fontSize: "18px !important"
                            },
                            "& sup": {
                              fontSize: "10px",
                              lineHeight: 0
                            },
                            ...verticalPkgNamesOn ? {
                              height: "fit-content"
                            } : {
                              width: "100%",
                              paddingTop: "6px"
                            }
                          }}
                        />
                      </DtvBox>
                      <DtvBox sx={{ ...verticalPkgNamesOn ? { height: "100%" } : { width: "100%" } }}>
                        <DtvText
                          content={packageConfig?.channelCount || packag?.numOfChannels}
                          className="package-chnl-count" // !! for vertical headers
                          sx={{
                            fontFamily: "roboto-regular",
                            whiteSpace: "nowrap",
                            fontSize: "14px !important",
                            textAlign: "left",
                            color: theming?.packageTiles?.[pkgInd]?.packageChannelsText,
                            lineHeight: "18.2px",
                            fontWeight: "500 !important",
                            ...(verticalPkgNamesOn ? { height: "fit-content" } : { width: "100%" })
                          }}
                        />
                      </DtvBox>
                    </DtvGrid>
                    {ctaProps && (
                      <DtvBox sx={ssrStyles.header.shopBtnContainer}>
                        <DtvButton
                          content={ctaProps?.label || "Shop"}
                          variant={ctaProps?.variant || "uxprimary" as IDtvButtonVariation}
                          mode={ctaProps?.mode || "dark" as IDtvMode}
                          label={`${ctaProps?.label}`}
                          // [WAT-12043 / YFH-AMP119] All four package tiles
                          // rendered the same "Link to Shop" label. Screen
                          // readers announce "link" from the role, so "Link
                          // to" was redundant, AND the four buttons became
                          // indistinguishable when heard out of context.
                          // Compose from the package name so each row's
                          // link stands on its own: "Shop ENTERTAINMENT",
                          // "Shop CHOICE", etc.
                          ariaLabel={`${ctaProps?.label || "Shop"} ${name}`}
                          target="_parent"
                          targeturl={ctaProps?.targeturl}
                          googleAnalyticsData = {getAnalyticsAttrs(
                            `${ctaProps?.label}_${name}`,
                            LINK_POSITION_BODY,
                            ctaProps?.targeturl?.includes("directv.com") ?
                              ctaProps?.targeturl :
                              `https://www.directv.com/${ctaProps?.targeturl}`
                          )}
                          {...ctaProps}
                          sx={ssrStyles.header.shopBtn}
                        />
                      </DtvBox>
                    )}
                  </Paper>
                </DtvBox>
              );
              } catch (error) {
                console.error("[PackagesGrid] ERROR in package loop: ", error);
                return null;
              }
            })}
          </DtvBox>
          {/* MyFreeDirectv Banner */}
          {myFreeDirectvBanner && (
            <DtvBox
              id="ChnlListingPage-MyFreeDtv-container"
              component="tr"
              sx={{
                ...ssrStyles.grid.myFreeDtvContainer,
                background: theming?.headerScrollHideBg
              }}>
              <DtvBox component="td" sx={ssrStyles.grid.tdFullWidthOverride}>
                <MyFreeDtvBanner
                  {...myFreeDirectvBanner}
                  channels={myFreeDtvChannels}
                  theming={theming?.myFreeDtvBanner}
                />
              </DtvBox>
            </DtvBox>
          )}
        </TableHead>

        <TableBody id="tableBody">
          {/* [WAT-11256] AC1/AC3/AC4/AC5: an active search always renders this single
              flat, deduplicated list — no accordion headers/collapse control, no
              per-view-mode branching — regardless of which sort mode was selected
              before the search started. */}
          {searchListActive ? (
            showSearchNoMatch ? noMatchElement : (
              <DtvBox component="tr" sx={{ background: theming?.gridBackground }}>
                <DtvBox component="td" sx={ssrStyles.grid.tdFullWidthOverride}>
                  {/* [WAT-11256 S1] A consolidation failure is no longer a full
                      replacement of this branch — it used to short-circuit BEFORE this
                      container, which meant a search that had independent SVOD and/or
                      MyFree matches lost those too just because the (separate, feed-
                      only) consolidation step threw. SVOD rows, consolidatedSearchChannels
                      (empty on failure — see consolidatedSearch's catch branch), and the
                      MyFree block below are unaffected by searchConsolidationFailed.
                      [round-6] When OTHER results still render (hasOtherMatches), the
                      "Search results" <h2> stays mounted (so the MyFree <h3> below it
                      isn't orphaned) with the now partial-failure-worded error text
                      alongside it — a sighted AND screen-reader user must not be told
                      "we found nothing" while rows are visibly present. */}
                  {searchConsolidationFailed ? (
                    hasOtherMatches ? (
                      <>
                        {searchResultsMessageElement}
                        {gridMessageText(searchErrorMsg)}
                      </>
                    ) : gridMessageText(searchErrorMsg)
                  ) : (
                    searchResultsMessageElement
                  )}
                  {/* Included subscriptions (SVODs) lead the search results, ahead of
                      the consolidated channel list — gated on isEmbeddedSvodsEnabled to
                      mirror the parent's INCLUDED_SUBSCRIPTIONS category gate
                      (ChnlListingPage.tsx: only unshifts that category "if
                      isEmbeddedSvodsEnabled"). Flag-off must stay byte-identical to prod. */}
                  {hasSvodMatches && embeddedSvods?.map((svod, cIx: number) => (
                    <SvodChannelRow
                      key={`svod-${svod?.name}-${cIx}`}
                      channel={svod}
                      chnlPackageData={chnlPackageData}
                      packageLength={packagesLength}
                      svodRowWidth={svodRowWidth}
                      packageIndicatorIconMap={packageIndicatorIconMap}
                      borderColor={theming?.gridBorder}
                      textColor={theming?.gridText}
                    />
                  ))}
                  {consolidatedSearchChannels?.map((channel, cIx: number) => {
                    // [WAT-11256] A consolidated row can carry merged indicators from both
                    // an INCLUDED and an ADD_ON/PROMO bucket entry — style it as an
                    // add-on/promo row (CMS-authored gridAddOnPromoText/Background, same
                    // tokens the browse-view ADD_ON_CHANNELS section uses) only on POSITIVE
                    // evidence of an add-on/promo indicator. Absence of IN alone is NOT
                    // enough — empty/all-null indicators must default to the neutral
                    // included styling, not the "costs extra" one.
                    const hasAddOnOrPromoIndicator = channel?.indicators?.some(
                      (ind: PACKAGE_INDICATORS) => ind === PACKAGE_INDICATORS.ADDON || ind === PACKAGE_INDICATORS.PROMO
                    );
                    const hasInIndicator = channel?.indicators?.includes(PACKAGE_INDICATORS.IN);
                    const isAddOnOnlyRow = hasAddOnOrPromoIndicator && !hasInIndicator;
                    const rowTextColor = isAddOnOnlyRow ? theming?.gridAddOnPromoText : theming?.gridText;
                    const rowBackground = isAddOnOnlyRow ? theming?.gridAddOnPromoBackground : theming?.gridBackground;
                    const rowElement = (
                      <ChannelRow
                        channel={channel}
                        chnlPackageData={chnlPackageData}
                        mainColWidthStyle={mainColWidthStyle}
                        packageIndicatorIconMap={packageIndicatorIconMap}
                        borderColor={theming?.gridBorder}
                        textColor={rowTextColor}
                        rsnTierLegalTextObj={rsnTierLegalTextObj}
                      />
                    );
                    return cIx > searchLazyLoadCutoff ? (
                      <LazyLoad
                        key={channel?.uuid}
                        offset={2500} debounce={25} once
                        style={{
                          minHeight: "108px",
                          background: rowBackground,
                          borderBottom: `1px solid ${theming?.gridBorder}`
                        }}>
                        {rowElement}
                      </LazyLoad>
                    ) : (
                      <DtvBox key={channel?.uuid} sx={{ background: rowBackground }}>
                        {rowElement}
                      </DtvBox>
                    );
                  })}
                  {/* [WAT-11256] MyFree DIRECTV search matches — rendered as their own
                      labelled, non-collapsible block (mirrors the browse-view MyFree
                      accordion's label/subheading/logo/CTA) rather than folded into the
                      flat consolidated list above, since MyFree's synthetic all-IN
                      indicators are only truthful next to that context. No expand/collapse
                      control, matching AC3 — this block is always fully shown. */}
                  {hasMyFreeMatches && (
                    <DtvBox
                      id="chlup-search-myfree-section"
                      sx={{ width: "100%" }}
                    >
                      <DtvBox
                        id="chlup-search-myfree-header"
                        sx={{
                          width: "100%",
                          // Matches SearchResultsMessage's own padding scale
                          // (DtvTheme.spacing(3)/(4) = 12px/16px) so both bands' left
                          // text edges align — this used to be a raw, off-scale 18px.
                          padding: `${DtvTheme.spacing(3)} ${DtvTheme.spacing(4)}`,
                          // [WAT-11256 BLOCKER] Re-establishes the "Search results"
                          // heading's own background band (SearchResultsMessage pairs
                          // theming?.sectionHeaders?.background with that same object's
                          // textColor on its OWN root). Scoped to just this label bar —
                          // NOT the whole section — so the channel rows below keep
                          // following theming?.gridBackground (often "transparent",
                          // letting the page's own background show through) instead of
                          // inheriting this light band underneath them.
                          backgroundColor: theming?.sectionHeaders?.background
                        }}>
                        <DtvText
                          content={myFreeConfig?.myFreeCategory?.value || "MyFree DIRECTV"}
                          component="h3"
                          variant="body2"
                          sx={{
                            fontFamily: "roboto-regular",
                            fontSize: "16px",
                            fontWeight: 400,
                            // Same color as the "Search results" heading — the SVOD
                            // row map and the entire consolidated channel list render
                            // between the two (this is a deliberate long-range color
                            // pairing via theming?.sectionHeaders, not local adjacency;
                            // SearchResultsMessage reads the same object's textColor)
                            // — now paired with the matching background set above.
                            color: theming?.sectionHeaders?.textColor
                          }}
                        />
                      </DtvBox>
                      <MyFreeInfoBlock
                        idPrefix="chlup-search-myfree-section"
                        // [WAT-11256 round-6 S14] Explicit, distinct id for THIS
                        // (search) consumer — the component's default is the legacy
                        // `myfreeDtv-info-container` id the browse-view accordion has
                        // always rendered, which off-repo GTM/QA selectors may key on
                        // and must not silently change.
                        bannerId="chlup-search-myfree-section-info-container"
                        myFreeConfig={myFreeConfig}
                        isSatelliteChlup={isSatelliteChlup}
                        subheadingTextColor={theming?.sectionHeaders?.textColor}
                        bannerBackground={theming?.myFreeDtvBanner?.background}
                        // [WAT-11717] Unlike the browse-view accordion (CategoryHeader.tsx,
                        // default DtvTheme.spacing(3)), the header label above and this
                        // banner are two separately-backgrounded boxes here — any margin
                        // between them rendered as a bare white gap rather than breathing
                        // room, so the search view collapses it to flush edges instead.
                        bannerMarginTop={DtvTheme.spacing(0)}
                        // [WAT-11774] Zero the SUBHEADING's margin too. Zeroing only the
                        // banner's left the bare strip on satellite CHLUP whenever a
                        // subheading is authored AND a MyFree row matches the search — the
                        // box above the banner still contributed its own hardcoded gap.
                        subheadingMarginTop={DtvTheme.spacing(0)}
                      />
                      {myFreeSearchMatches?.map((channel, cIx: number) => (
                        // [WAT-11256] theming?.gridBackground — the same CMS-driven
                        // token every other row in this grid (consolidated search rows,
                        // browse-view AZ/category rows) is painted with. Previously
                        // unset here, so this row silently fell back to whatever sits
                        // behind it instead of following the grid's own theme.
                        <DtvBox key={channel?.uuid || `myfree-search-${cIx}`} sx={{ background: theming?.gridBackground }}>
                          <ChannelRow
                            channel={channel}
                            chnlPackageData={chnlPackageData}
                            mainColWidthStyle={mainColWidthStyle}
                            packageIndicatorIconMap={packageIndicatorIconMap}
                            borderColor={theming?.gridBorder}
                            textColor={theming?.gridText}
                            rsnTierLegalTextObj={rsnTierLegalTextObj}
                          />
                        </DtvBox>
                      ))}
                    </DtvBox>
                  )}
                </DtvBox>
              </DtvBox>
            )
          ) : showBrowseNoMatch ? noMatchElement : contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER ? (
            displayCategories?.length > 0 &&
            displayCategories?.map((category, ctgyIndex) => {
              try {
                const { key: ctgKey, value: tableCtgyValue } = category;
                const isAddOnCtgy = ctgKey === "ADD_ON_CHANNELS";
                const isMyFreeDtvCtgy = ctgKey === "MYFREE_DTV";
                let showAccordion = false;

                  if (ctgKey === "INCLUDED_SUBSCRIPTIONS" && embeddedSvods.length > 0) {
                    showAccordion = true; // always show Included Subscriptions
                  } else if (ctgKey !== "ADD_ON_CHANNELS") {
                    showAccordion =
                    displayChannels?.[ctgKey]?.length > 0 &&
                      Array.isArray(displayChannels?.[ctgKey]);
                  } else {
                    showAccordion =
                      Object.keys(displayChannels?.ADD_ON_CHANNELS || {})?.length > 0;
                  }

                if (showAccordion) {
                  return (
                    // [WAT-12043 / YFH-AMP109] Plain native <tr>/<td> here.
                    // A prior role="rowgroup" + role="presentation" layering
                    // nested a rowgroup inside <TableBody> (already a
                    // rowgroup) whose only descendants were an Accordion
                    // button + details — no `row` children — tripping axe
                    // `aria-required-parent` / `aria-required-children`.
                    // CategoryHeader's <h2> is what screen-reader users
                    // navigate the accordion sections by.
                    <DtvBox
                      key={`CHLUP-az-section-${tableCtgyValue}`}
                      component="tr"
                      sx={{ background: theming?.gridBackground }}>
                      <DtvBox component="td" sx={ssrStyles.grid.tdFullWidthOverride}>
                        <Accordion
                          key={`CHLUP-az-acc-${tableCtgyValue}`}
                          id={`CHLUP-az-acc-${tableCtgyValue}`}
                          TransitionProps={{
                            timeout: 400,
                            unmountOnExit: true
                          }}
                          expanded={accStateByAZ[ctgyIndex]?.expanded ?? false}
                          sx={ssrStyles.grid.muiAccordion}
                        >
                          <CategoryHeader
                            index={ctgyIndex}
                            id={`CHLUP-az-accsummary-${ctgyIndex}`}
                            ariaControls={`CHLUP-az-acc-details-${ctgyIndex}`}
                            isAddOnCtgyHead={isAddOnCtgy}
                            isMyFreeDtvCtgyHead={isMyFreeDtvCtgy}
                            label={tableCtgyValue}
                            expanded={accStateByAZ[ctgyIndex]?.expanded ?? false}
                            handleClick={handleCtgyHeaderClick}
                            headerHeight={headerHeight}
                            addOnPromoLogoInfo={addOnPromoLogoInfo}
                            theming={theming?.sectionHeaders}
                            myfreeBgColor={theming?.myFreeDtvBanner?.background}
                            myFreeConfig={myFreeConfig}
                            isSatelliteChlup={isSatelliteChlup}
                          />
                          <AccordionDetails
                            id={`CHLUP-az-acc-details-${ctgyIndex}`}
                            sx={{
                              ...ssrStyles.grid.muiAccordionDetails,
                              background: isAddOnCtgy ? theming?.gridAddOnPromoBackground : theming?.gridBackground
                            }}
                          >
                              {displayChannels?.[ctgKey]?.map((channel, cIx: number) => {
                                if (channel?.name === undefined || channel?.chnlNum === undefined) {
                                  console.warn("[PackagesGrid] channel name/chnlNum is undefined, skipping", channel);
                                }

                                if (ctgyIndex > 0 || cIx > 15) {
                                  return (
                                    <LazyLoad
                                      key={channel?.uuid}
                                      offset={2500} debounce={25} once
                                      style={{
                                        minHeight: "108px",
                                        background: theming?.gridBackground,
                                        borderBottom: `1px solid ${theming?.gridBorder}`
                                      }}>
                                      <ChannelRow
                                        channel={channel}
                                        chnlPackageData={chnlPackageData}
                                        mainColWidthStyle={mainColWidthStyle}
                                        packageIndicatorIconMap={packageIndicatorIconMap}
                                        borderColor={theming?.gridBorder}
                                        textColor={isAddOnCtgy ? theming?.gridAddOnPromoText : theming?.gridText}
                                        rsnTierLegalTextObj={rsnTierLegalTextObj}
                                      />
                                    </LazyLoad>
                                  );
                                }
                                return (
                                  <ChannelRow key={channel?.uuid}
                                    channel={channel}
                                    chnlPackageData={chnlPackageData}
                                    mainColWidthStyle={mainColWidthStyle}
                                    packageIndicatorIconMap={packageIndicatorIconMap}
                                    borderColor={theming?.gridBorder}
                                    textColor={isAddOnCtgy ? theming?.gridAddOnPromoText : theming?.gridText}
                                    rsnTierLegalTextObj={rsnTierLegalTextObj}
                                  />
                                );
                              })}
                              {ctgKey === "INCLUDED_SUBSCRIPTIONS" &&
                                embeddedSvods?.map((svod, cIx: number) => (
                                    <SvodChannelRow
                                      key={`svod-${svod?.name}-${cIx}`}
                                      channel={svod}
                                      chnlPackageData={chnlPackageData}
                                      packageLength={packagesLength}
                                      svodRowWidth={svodRowWidth}
                                      packageIndicatorIconMap={packageIndicatorIconMap}
                                      borderColor={theming?.gridBorder}
                                      textColor={isAddOnCtgy ? theming?.gridAddOnPromoText : theming?.gridText}
                                    />
                                  ))
                              }
                          </AccordionDetails>
                        </Accordion>
                      </DtvBox>
                    </DtvBox>
                  );
                }
                return null;
              } catch (error) {
                console.error("[PackagesGrid] ERROR in AZ cat loop: ", error);
                return null;
              }
            })
          ) : contentViewMode?.key === CHANNEL_LINEUP_VIEW.CATEGORY ? (
            categoriesCategorical?.length > 0 &&
            categoriesCategorical?.map((category, ctgyIndex) => {
              try {
              const { key: ctgKey, value: tableCtgyValue } = category;
              const isAddOnCtgy = ctgKey === "ADD_ON_CHANNELS";
              const isMyFreeDtvCtgy = ctgKey === "MYFREE_DTV";

              let showAccordion = false;
              if (ctgKey === "INCLUDED_SUBSCRIPTIONS" && embeddedSvods.length > 0) {
                showAccordion = true; // always show Included Subscriptions
              } else if (ctgKey !== "ADD_ON_CHANNELS") {
                showAccordion =
                  channelsCategorical?.[ctgKey]?.length > 0 &&
                  Array.isArray(channelsCategorical?.[ctgKey]);
              } else {
                showAccordion =
                  Object.keys(channelsCategorical?.ADD_ON_CHANNELS || {})?.length > 0;
              }

              if (showAccordion) {
                return (
                  // [WAT-12043 / YFH-AMP109] Plain native <tr>/<td> —
                  // same rationale as the a-z branch above.
                  <DtvBox
                    key={`CHLUP-ctg-section-${tableCtgyValue}`}
                    component="tr">
                    <DtvBox component="td" sx={ssrStyles.grid.tdFullWidthOverride}>
                      <Accordion
                        key={`CHLUP-ctg-acc-${tableCtgyValue}`}
                        id={`CHLUP-ctg-acc-${tableCtgyValue}`}
                        TransitionProps={{
 timeout: 400,
unmountOnExit: true
}}
                        expanded={accStateByCategory[ctgyIndex]?.expanded ?? false}
                        sx={ssrStyles.grid.muiAccordion}
                      >
                        <CategoryHeader
                          index={ctgyIndex}
                          id={`CHLUP-ctg-accsummary-${ctgyIndex}`}
                          ariaControls={`CHLUP-ctg-acc-details-${ctgyIndex}`}
                          isAddOnCtgyHead={isAddOnCtgy}
                          isMyFreeDtvCtgyHead={isMyFreeDtvCtgy}
                          label={tableCtgyValue}
                          expanded={accStateByCategory[ctgyIndex]?.expanded ?? false}
                          handleClick={handleCtgyHeaderClick}
                          headerHeight={headerHeight}
                          addOnPromoLogoInfo={addOnPromoLogoInfo}
                          theming={theming?.sectionHeaders}
                          myfreeBgColor={theming?.myFreeDtvBanner?.background}
                          myFreeConfig={myFreeConfig}
                          isSatelliteChlup={isSatelliteChlup}
                        />
                        <AccordionDetails
                          id={`CHLUP-ctg-acc-details-${ctgyIndex}`}
                          sx={{
                            ...ssrStyles.grid.muiAccordionDetails,
                            background: isAddOnCtgy ? theming?.gridAddOnPromoBackground : theming?.gridBackground
                          }}
                        >
                            {ctgKey === "ADD_ON_CHANNELS" ? (
                              (channelsCategorical?.ADD_ON_CHANNELS && Object.keys(channelsCategorical.ADD_ON_CHANNELS).length > 0) && Object.keys(channelsCategorical?.ADD_ON_CHANNELS)?.map(subCatKey => {
                                if (channelsCategorical?.ADD_ON_CHANNELS?.[subCatKey]?.length > 0) {
                                  const chnls = channelsCategorical?.ADD_ON_CHANNELS?.[subCatKey];
                                  const displayName = categoriesCategorical?.find(cat => cat.key === subCatKey)?.value;

                                  return (
                                    <React.Fragment key={`addon-head-${displayName}`}>
                                      <DtvBox
                                        id={`CHLUP-addon-head-${displayName}`}
                                        style={{
                                          background: theming?.sectionHeaders?.background,
                                          border: theming?.sectionHeaders?.border || "none",
                                          width: "100%",
                                          margin: "0 !important",
                                          padding: "8px 0px 8px 18px"
                                      }}>
                                        <DtvText
                                          content={displayName}
                                          variant="smalltext"
                                          sx={{
 fontSize: "14px !important",
color: theming?.sectionHeaders?.textColor
}}
                                        />
                                      </DtvBox>
                                      {chnls?.map(channel => {
                                          if (channel?.name === undefined || channel?.chnlNum === undefined) {
                                            console.warn("[PackagesGrid] channel name/chnlNum is undefined, skipping", channel);
                                          }
                                          return (
                                            <LazyLoad
                                              key={channel?.uuid}
                                              offset={2500} debounce={25} once
                                              style={{
                                                minHeight: "108px",
                                                background: theming?.gridBackground,
                                                borderBottom: `1px solid ${theming?.gridBorder}`
                                              }}>
                                              <ChannelRow
                                                channel={channel}
                                                chnlPackageData={chnlPackageData}
                                                mainColWidthStyle={mainColWidthStyle}
                                                packageIndicatorIconMap={packageIndicatorIconMap}
                                                borderColor={theming?.gridBorder}
                                                textColor={theming?.gridAddOnPromoText}
                                                rsnTierLegalTextObj={rsnTierLegalTextObj}
                                              />
                                            </LazyLoad>
                                          );
                                      })}
                                    </React.Fragment>
                                  );
                                }
                                return null;
                              })
                            ) : ctgKey === "INCLUDED_SUBSCRIPTIONS" ? (
                              embeddedSvods?.map((svod, cIx: number) => (
                                  <SvodChannelRow
                                    key={`svod-${svod?.name}-${cIx}`}
                                    channel={svod}
                                    chnlPackageData={chnlPackageData}
                                    packageLength={packagesLength}
                                    svodRowWidth={svodRowWidth}
                                    packageIndicatorIconMap={packageIndicatorIconMap}
                                    borderColor={theming?.gridBorder}
                                    textColor={theming?.gridAddOnPromoText}
                                  />
                                ))
                            ) : channelsCategorical?.[ctgKey]?.map(channel => {
                              if (channel?.name === undefined || channel?.chnlNum === undefined) {
                                console.warn("[PackagesGrid] channel name/chnlNum is undefined, skipping", channel);
                              }
                              if (ctgyIndex > 0) {
                                return (
                                  <LazyLoad
                                    key={channel?.uuid}
                                    offset={2500} debounce={25} once
                                    style={{
                                      minHeight: "108px",
                                      backgroundColor: isAddOnCtgy ? theming?.gridAddOnPromoBackground : theming?.gridBackground,
                                      borderBottom: `1px solid ${theming?.gridBorder}`
                                    }}>
                                    <ChannelRow
                                      channel={channel}
                                      chnlPackageData={chnlPackageData}
                                      mainColWidthStyle={mainColWidthStyle}
                                      packageIndicatorIconMap={packageIndicatorIconMap}
                                      borderColor={theming?.gridBorder}
                                      textColor={theming?.gridAddOnPromoText}
                                      rsnTierLegalTextObj={rsnTierLegalTextObj}
                                    />
                                  </LazyLoad>
                                );
                              }
                              return (
                                <ChannelRow key={channel?.uuid}
                                  channel={channel}
                                  chnlPackageData={chnlPackageData}
                                  mainColWidthStyle={mainColWidthStyle}
                                  packageIndicatorIconMap={packageIndicatorIconMap}
                                  borderColor={theming?.gridBorder}
                                  textColor={theming?.gridAddOnPromoText}
                                  rsnTierLegalTextObj={rsnTierLegalTextObj}
                                />
                              );
                            })}
                        </AccordionDetails>
                      </Accordion>
                    </DtvBox>
                  </DtvBox>
                );
              }
              return null;
              } catch (error) {
                console.error("[PackagesGrid] ERROR in Categorical cat loop: ", error);
                return null;
              }
            })
          ) : null}
        </TableBody>
      </Table>
    </DtvBox>
  );
}

export const MemoizedPackageComparisonGrid = React.memo(PackageComparisonGrid);
